/**
 * Touch and mobile-friendly drag-and-drop backend configuration
 * Provides unified support for both desktop and touch devices
 */

import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { MultiBackend, createTransition } from 'dnd-multi-backend';

/**
 * Mobile device detection
 */
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    ('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0);
};

/**
 * Touch support detection
 */
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || 
         navigator.maxTouchPoints > 0 ||
         // @ts-ignore
         navigator.msMaxTouchPoints > 0;
};

/**
 * Multi-backend configuration for unified desktop and mobile support
 */
export const TouchDragBackend = MultiBackend;

export const TouchDragBackendOptions = {
  backends: [
    {
      id: 'html5',
      backend: HTML5Backend,
      transition: createTransition('mousedown', (event: any) => {
        return !isTouchDevice() || (event.type === 'mousedown' && !('touches' in event));
      }),
    },
    {
      id: 'touch',
      backend: TouchBackend,
      options: {
        enableMouseEvents: true, // ✅ Enable mouse events for hybrid devices
        // Improved touch handling for CSS Grid
        delayTouchStart: 150,
        delayMouseStart: 0,
        touchSlop: 10,
      },
      transition: createTransition('touchstart', (event: any) => {
        return isTouchDevice() && (event.type === 'touchstart' || 'touches' in event);
      }),
    },
  ],
};

/**
 * Grid coordinate conversion for touch events
 * Converts touch coordinates to CSS Grid column positions
 */
export const convertTouchToGridCoordinates = (
  touchX: number,
  touchY: number,
  gridContainer: HTMLElement,
  totalDisplayHours: number,
  intervalMinutes: number = 15
): { column: number; row: number } => {
  const containerRect = gridContainer.getBoundingClientRect();
  const relativeX = touchX - containerRect.left;
  const relativeY = touchY - containerRect.top;
  
  // Calculate grid dimensions
  const intervalsPerHour = 60 / intervalMinutes;
  const totalIntervals = totalDisplayHours * intervalsPerHour;
  const columnWidth = containerRect.width / totalIntervals;
  const rowHeight = 60; // Standard row height
  
  // Convert to grid coordinates (1-indexed for CSS Grid)
  const column = Math.max(1, Math.min(Math.floor(relativeX / columnWidth) + 1, totalIntervals + 1));
  const row = Math.max(1, Math.floor(relativeY / rowHeight) + 1);
  
  return { column, row };
};

/**
 * Enhanced touch event handling for shift calendar
 */
export interface TouchShiftDragConfig {
  onTouchStart?: (event: TouchEvent, shiftId: string) => void;
  onTouchMove?: (event: TouchEvent, shiftId: string) => void;
  onTouchEnd?: (event: TouchEvent, shiftId: string) => void;
  onTouchCancel?: (event: TouchEvent, shiftId: string) => void;
}

/**
 * Touch gesture recognition for shift operations
 */
export class TouchGestureHandler {
  private startX = 0;
  private startY = 0;
  private startTime = 0;
  private element: HTMLElement | null = null;
  
  constructor(private config: TouchShiftDragConfig) {}
  
  handleTouchStart = (event: TouchEvent, shiftId: string) => {
    const touch = event.touches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.startTime = Date.now();
    this.element = event.target as HTMLElement;
    
    this.config.onTouchStart?.(event, shiftId);
  };
  
  handleTouchMove = (event: TouchEvent, shiftId: string) => {
    event.preventDefault(); // Prevent scrolling during drag
    this.config.onTouchMove?.(event, shiftId);
  };
  
  handleTouchEnd = (event: TouchEvent, shiftId: string) => {
    const touch = event.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const endTime = Date.now();
    
    const deltaX = Math.abs(endX - this.startX);
    const deltaY = Math.abs(endY - this.startY);
    const deltaTime = endTime - this.startTime;
    
    // Distinguish between tap, long press, and drag
    const isQuickTap = deltaTime < 200 && deltaX < 10 && deltaY < 10;
    const isLongPress = deltaTime > 800 && deltaX < 10 && deltaY < 10;
    const isDrag = deltaX > 20 || deltaY > 20;
    
    if (isQuickTap) {
      // Handle quick tap (selection/edit)
      console.log('Touch: Quick tap detected on shift', shiftId);
    } else if (isLongPress) {
      // Handle long press (context menu)
      console.log('Touch: Long press detected on shift', shiftId);
    } else if (isDrag) {
      // Handle drag operation
      console.log('Touch: Drag operation completed on shift', shiftId);
    }
    
    this.config.onTouchEnd?.(event, shiftId);
    
    // Reset state
    this.startX = 0;
    this.startY = 0;
    this.startTime = 0;
    this.element = null;
  };
  
  handleTouchCancel = (event: TouchEvent, shiftId: string) => {
    this.config.onTouchCancel?.(event, shiftId);
    
    // Reset state
    this.startX = 0;
    this.startY = 0;
    this.startTime = 0;
    this.element = null;
  };
}

/**
 * Mobile-specific CSS Grid positioning utilities
 */
export const mobileGridPositioning = {
  /**
   * Adjust cell widths for mobile screens
   */
  getMobileCellWidth: (screenWidth: number, totalColumns: number): number => {
    const minCellWidth = 40; // Minimum touch target size
    const maxCellWidth = 80; // Maximum for readability
    const calculatedWidth = screenWidth / totalColumns;
    
    return Math.max(minCellWidth, Math.min(maxCellWidth, calculatedWidth));
  },
  
  /**
   * Adjust row heights for mobile screens
   */
  getMobileRowHeight: (): number => {
    return isMobile() ? 50 : 60; // Slightly shorter on mobile
  },
  
  /**
   * Calculate optimal zoom level for mobile
   */
  getMobileZoomLevel: (containerWidth: number, totalDisplayHours: number): number => {
    const minZoom = 0.7;
    const maxZoom = 1.0;
    const intervalsPerHour = 4; // 15-minute intervals
    const totalIntervals = totalDisplayHours * intervalsPerHour;
    const idealCellWidth = 50;
    const calculatedZoom = (containerWidth / (totalIntervals * idealCellWidth));
    
    return Math.max(minZoom, Math.min(maxZoom, calculatedZoom));
  }
};